import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useCampaign } from '../context/CampaignContext';
import { t } from '../lib/i18n';
import { MainLayout } from '../components/layout/MainLayout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { formatDate, getInitials, getAvatarColor, getRoleDisplay, isGameMaster } from '../lib/utils';
import { toast } from 'sonner';
import {
  Calendar,
  Compass,
  Users,
  MapPin,
  Plus,
  Copy,
  ChevronRight,
  Loader2,
  Gamepad2,
  Store,
  Save,
  Link2,
  LogOut,
} from 'lucide-react';
import { PlayerDashboardView } from '../components/campaign/PlayerDashboardView';
import { PublishTemplateDialog } from '../components/marketplace/PublishTemplateDialog';

const API_URL = '/api';

export default function CampaignDetailPage() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const { user, language } = useAuth();
  const { currentCampaign, createInvite, loading: campaignLoading } = useCampaign();
  
  const [sessions, setSessions] = useState([]);
  const [quests, setQuests] = useState([]);
  const [npcs, setNpcs] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteCode, setInviteCode] = useState('');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [generatingInvite, setGeneratingInvite] = useState(false);
  const [systemInfo, setSystemInfo] = useState(null);
  const [canPublish, setCanPublish] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [savingToTemplate, setSavingToTemplate] = useState(false);
  const [saveTemplateConfirm, setSaveTemplateConfirm] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);

  const userRole = currentCampaign?.members?.find(m => m.user_id === user?.id)?.role;
  const isMJ = isGameMaster(userRole);

  useEffect(() => {
    if (isMJ) {
      axios.get('/api/marketplace/can-publish')
        .then(r => setCanPublish(r.data?.can_publish || false))
        .catch(() => setCanPublish(false));
    }
  }, [isMJ]);

  const linkedTemplateId = currentCampaign?.linked_template_id;

  const handleSaveToTemplate = async () => {
    if (!linkedTemplateId) return;
    setSavingToTemplate(true);
    try {
      await axios.post(`/api/admin/marketplace/templates/${linkedTemplateId}/save-campaign`);
      toast.success(t('marketplace_save_back_success', language));
      setSaveTemplateConfirm(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'fr' ? 'Erreur lors de la sauvegarde' : 'Save failed'));
    } finally {
      setSavingToTemplate(false);
    }
  };

  const handleLeaveCampaign = async () => {
    setLeaving(true);
    try {
      await axios.post(`${API_URL}/campaigns/${campaignId}/leave`);
      toast.success(t('campaign_leave_success', language));
      setLeaveOpen(false);
      navigate('/campaigns');
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'fr' ? 'Erreur' : 'Error'));
    } finally {
      setLeaving(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [sessionsRes, questsRes, npcsRes, locationsRes] = await Promise.all([
          axios.get(`${API_URL}/campaigns/${campaignId}/sessions`),
          axios.get(`${API_URL}/campaigns/${campaignId}/quests`),
          axios.get(`${API_URL}/campaigns/${campaignId}/npcs`),
          axios.get(`${API_URL}/campaigns/${campaignId}/locations`)
        ]);
        setSessions(sessionsRes.data);
        setQuests(questsRes.data);
        setNpcs(npcsRes.data);
        setLocations(locationsRes.data);
      } catch (error) {
        console.error('Error fetching campaign data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (campaignId) {
      fetchData();
    }
  }, [campaignId]);

  useEffect(() => {
    if (currentCampaign?.system_id) {
      axios.get(`/api/game-systems/${currentCampaign.system_id}`)
        .then(r => setSystemInfo(r.data))
        .catch(() => setSystemInfo(null));
    } else {
      setSystemInfo(null);
    }
  }, [currentCampaign?.system_id]);

  const handleGenerateInvite = async () => {
    setGeneratingInvite(true);
    try {
      const result = await createInvite(campaignId);
      setInviteCode(result.code);
    } catch (error) {
      toast.error(t('error_generic', language));
    } finally {
      setGeneratingInvite(false);
    }
  };

  const copyInviteCode = () => {
    navigator.clipboard.writeText(inviteCode);
    toast.success(language === 'fr' ? 'Code copié !' : 'Code copied!');
  };

  const activeQuests = quests.filter(q => q.status === 'in_progress' || q.status === 'known');
  const recentSessions = sessions.slice(0, 3);

  if (campaignLoading || !currentCampaign) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-8 animate-fade-in" data-testid="campaign-detail-page">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div>
            <h1 className="font-cinzel text-3xl font-bold">{currentCampaign.name}</h1>
            <p className="text-muted-foreground mt-1">
              {currentCampaign.universe || 'D&D 5e'}
              {currentCampaign.style && ` • ${currentCampaign.style}`}
            </p>
            {currentCampaign.description && (
              <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
                {currentCampaign.description}
              </p>
            )}
            <div className="flex items-center gap-2 mt-3">
              {systemInfo ? (
                <Badge variant="secondary" className="flex items-center gap-1 text-xs">
                  <Gamepad2 className="h-3 w-3" />
                  {systemInfo.name}
                  {systemInfo.version && ` ${systemInfo.version}`}
                </Badge>
              ) : (
                <Badge variant="outline" className="text-xs text-muted-foreground">
                  <Gamepad2 className="h-3 w-3 mr-1" />
                  {t('campaign_system_none', language)}
                </Badge>
              )}
            </div>
          </div>
          
          {isMJ && (
            <div className="flex flex-wrap items-center gap-2">
              {canPublish && (
                <Button variant="outline" onClick={() => setPublishOpen(true)}>
                  <Store className="mr-2 h-4 w-4" />
                  {t('marketplace_publish', language)}
                </Button>
              )}
            </div>
          )}

          {/* Non-MJ: leave button */}
          {!isMJ && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                className="border-destructive/30 text-destructive hover:bg-destructive/10"
                onClick={() => setLeaveOpen(true)}
              >
                <LogOut className="mr-2 h-4 w-4" />
                {t('campaign_leave', language)}
              </Button>
            </div>
          )}

          {canPublish && (
            <PublishTemplateDialog
              campaignId={campaignId}
              campaignName={currentCampaign?.name}
              universe={currentCampaign?.universe}
              open={publishOpen}
              onOpenChange={setPublishOpen}
              language={language}
            />
          )}
        </div>

        {/* Linked Template Banner */}
        {linkedTemplateId && isMJ && (
          <Card className="border-info/30 bg-info/5">
            <CardContent className="py-3 px-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 text-sm">
                  <Link2 className="h-4 w-4 text-info" />
                  <span className="font-medium text-info">
                    {t('marketplace_linked_template', language)}
                  </span>
                  <span className="text-muted-foreground">—</span>
                  <span className="text-muted-foreground text-xs">
                    {t('marketplace_save_back_desc', language)}
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-info/30 text-info hover:bg-info/10"
                  onClick={() => setSaveTemplateConfirm(true)}
                  disabled={savingToTemplate}
                >
                  {savingToTemplate ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="mr-2 h-4 w-4" />
                  )}
                  {t('marketplace_save_back', language)}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Player Dashboard View (non-MJ) */}
        {!isMJ && <PlayerDashboardView campaignId={campaignId} />}

        {/* GM Content below */}
        {isMJ && (<>
        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card
            className="glass-card card-interactive card-lift cursor-pointer"
            onClick={() => navigate(`/campaigns/${campaignId}/sessions`)}
            data-testid="sessions-stat"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Calendar className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold font-cinzel">{sessions.length}</p>
                  <p className="text-xs text-muted-foreground">{t('sessions', language)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className="glass-card card-interactive card-lift cursor-pointer"
            onClick={() => navigate(`/campaigns/${campaignId}/quests`)}
            data-testid="quests-stat"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-warning/10">
                  <Compass className="h-5 w-5 text-warning" />
                </div>
                <div>
                  <p className="text-2xl font-bold font-cinzel">{activeQuests.length}</p>
                  <p className="text-xs text-muted-foreground">{t('dashboard_active_quests', language)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className="glass-card card-interactive card-lift cursor-pointer"
            onClick={() => navigate(`/campaigns/${campaignId}/npcs`)}
            data-testid="npcs-stat"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-success/10">
                  <Users className="h-5 w-5 text-success" />
                </div>
                <div>
                  <p className="text-2xl font-bold font-cinzel">{npcs.length}</p>
                  <p className="text-xs text-muted-foreground">{t('npcs', language)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className="glass-card card-interactive card-lift cursor-pointer"
            onClick={() => navigate(`/campaigns/${campaignId}/locations`)}
            data-testid="locations-stat"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-info/10">
                  <MapPin className="h-5 w-5 text-info" />
                </div>
                <div>
                  <p className="text-2xl font-bold font-cinzel">{locations.length}</p>
                  <p className="text-xs text-muted-foreground">{t('locations', language)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Sessions */}
          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-cinzel text-xl font-bold">{t('dashboard_recent_sessions', language)}</h2>
              <Button variant="ghost" size="sm" onClick={() => navigate(`/campaigns/${campaignId}/sessions`)}>
                {t('view_all', language)}
                <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
            
            {loading ? (
              <Card className="glass-card">
                <CardContent className="p-6">
                  <div className="animate-pulse space-y-4">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="h-16 bg-muted rounded" />
                    ))}
                  </div>
                </CardContent>
              </Card>
            ) : recentSessions.length === 0 ? (
              <Card className="glass-card">
                <CardContent className="py-8 text-center">
                  <Calendar className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                  <p className="text-muted-foreground mb-4">
                    {language === 'fr' ? 'Aucune session pour le moment' : 'No sessions yet'}
                  </p>
                  {isMJ && (
                    <Button 
                      variant="outline" 
                      onClick={() => navigate(`/campaigns/${campaignId}/sessions`)}
                      data-testid="create-first-session"
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      {t('session_create', language)}
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {recentSessions.map((session) => (
                  <Card 
                    key={session.id} 
                    className="glass-card card-interactive cursor-pointer"
                    onClick={() => navigate(`/campaigns/${campaignId}/sessions/${session.id}`)}
                    data-testid={`session-${session.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-medium">{session.title}</h3>
                          <p className="text-sm text-muted-foreground">
                            {formatDate(session.scheduled_date, language === 'fr' ? 'fr-FR' : 'en-US')}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {session.is_published ? (
                            <span className="text-xs px-2 py-1 rounded-full bg-success/10 text-success">
                              {t('session_published', language)}
                            </span>
                          ) : isMJ ? (
                            <span className="text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground">
                              {t('session_draft', language)}
                            </span>
                          ) : null}
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Members */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-cinzel text-xl font-bold">{t('campaign_members', language)}</h2>
              {isMJ && (
                <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" data-testid="invite-btn">
                      <Plus className="mr-1.5 h-3.5 w-3.5" />
                      {t('campaign_invite', language)}
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle className="font-cinzel">{t('campaign_invite', language)}</DialogTitle>
                      <DialogDescription>
                        {language === 'fr'
                          ? 'Générez un code d\'invitation pour vos joueurs'
                          : 'Generate an invite code for your players'}
                      </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                      {inviteCode ? (
                        <div className="space-y-4">
                          <div className="flex items-center gap-2">
                            <code className="flex-1 p-3 bg-muted rounded-lg font-mono text-lg text-center">
                              {inviteCode}
                            </code>
                            <Button variant="outline" size="icon" onClick={copyInviteCode} data-testid="copy-code-btn">
                              <Copy className="h-4 w-4" />
                            </Button>
                          </div>
                          <p className="text-sm text-muted-foreground text-center">
                            {language === 'fr'
                              ? 'Partagez ce code avec vos joueurs'
                              : 'Share this code with your players'}
                          </p>
                        </div>
                      ) : (
                        <Button
                          onClick={handleGenerateInvite}
                          disabled={generatingInvite}
                          className="w-full"
                          data-testid="generate-code-btn"
                        >
                          {generatingInvite && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                          {language === 'fr' ? 'Générer un code' : 'Generate code'}
                        </Button>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </div>
            <Card className="glass-card">
              <CardContent className="p-4 space-y-3">
                {currentCampaign.members.map((member) => (
                  <div key={member.user_id} className="flex items-center gap-3" data-testid={`member-${member.user_id}`}>
                    <Avatar className="h-9 w-9">
                      <AvatarFallback className={getAvatarColor(member.user_id)}>
                        {getInitials(member.username)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{member.username}</p>
                      {member.character_name && (
                        <p className="text-xs text-muted-foreground truncate">{member.character_name}</p>
                      )}
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      member.role === 'owner' ? 'bg-primary/10 text-primary' : 'bg-secondary text-secondary-foreground'
                    }`}>
                      {getRoleDisplay(member.role, language)}
                    </span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Active Quests */}
        {activeQuests.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-cinzel text-xl font-bold">{t('dashboard_active_quests', language)}</h2>
              <Button variant="ghost" size="sm" onClick={() => navigate(`/campaigns/${campaignId}/quests`)}>
                {t('view_all', language)}
                <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {activeQuests.slice(0, 3).map((quest) => (
                <Card 
                  key={quest.id} 
                  className="glass-card card-interactive cursor-pointer"
                  onClick={() => navigate(`/campaigns/${campaignId}/quests/${quest.id}`)}
                  data-testid={`quest-${quest.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-medium">{quest.title}</h3>
                      <span className={`status-chip ${
                        quest.status === 'in_progress' ? 'status-in_progress' : 'status-known'
                      }`}>
                        {t(`quest_status_${quest.status}`, language)}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {quest.summary || (language === 'fr' ? 'Pas de description' : 'No description')}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      {/* End GM Content */}
      </>)}
      </div>
      {/* Dialog: confirmer la sauvegarde vers le template */}
      <Dialog open={saveTemplateConfirm} onOpenChange={setSaveTemplateConfirm}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-cinzel flex items-center gap-2">
              <Save className="h-5 w-5 text-info" />
              {t('marketplace_save_back', language)}
            </DialogTitle>
            <DialogDescription>
              {t('marketplace_save_back_confirm', language)}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveTemplateConfirm(false)}>
              {t('cancel', language)}
            </Button>
            <Button onClick={handleSaveToTemplate} disabled={savingToTemplate} className="fantasy-button">
              {savingToTemplate ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              {t('marketplace_save_back', language)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: quitter la campagne */}
      <Dialog open={leaveOpen} onOpenChange={setLeaveOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-cinzel flex items-center gap-2 text-destructive">
              <LogOut className="h-5 w-5" />
              {t('campaign_leave', language)}
            </DialogTitle>
            <DialogDescription>
              {t('campaign_leave_confirm', language)}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLeaveOpen(false)}>
              {t('cancel', language)}
            </Button>
            <Button
              variant="destructive"
              disabled={leaving}
              onClick={handleLeaveCampaign}
            >
              {leaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('campaign_leave', language)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </MainLayout>
  );
}

